import type { BrandName } from "@elit-hair/brands";
import {
    Button,
    DatePicker,
    EmptyState,
    ErrorState,
    Icon,
    Spinner,
    Tabs,
    TabsList,
    TabsTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarSearch, TbChevronDown, TbChevronLeft, TbChevronRight, TbX } from "react-icons/tb";
import { toast } from "sonner";
import { z } from "zod";

import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectGroup,
    MultiSelectItem,
    MultiSelectTrigger,
    MultiSelectValue,
} from "@/components/multi-select";
import { PageActions } from "@/components/page-actions";
import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { PERMISSIONS } from "@/constants/permissions";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useUsersByIdsBatch } from "@/hooks/queries/user.queries";
import { AppointmentBrandSwitchDialog } from "@/pages/appointments/components/appointment-brand-switch-dialog";
import { getAppointmentStartDateTime } from "@/pages/appointments/utils/appointment-selection.utils";
import { useCall } from "@/providers/hooks/use-call";
import {
    BOOKING_STATUS,
    type Booking,
    useBookings,
    usePublicBooking,
    useUpdateBooking,
} from "@/queries/booking.queries";
import { useEventTypes, useEventTypesRules } from "@/queries/event-type.queries";
import { useAvailableHostSlots } from "@/queries/slot.queries";
import { ApiError } from "@/utils/api-error";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";
import { getUserIdToLabelMap } from "@/utils/team.utils";

import { APPOINTMENT_SEARCH_MIN_LENGTH, AppointmentSearchDialog } from "./components/appointment-search-dialog";
import { AppointmentSearchInput } from "./components/appointment-search-input";
import { CalendarAppointmentsSelectionBar } from "./components/calendar-appointments-selection-bar";
import { CalendarAppointmentsSummaryBar } from "./components/calendar-appointments-summary-bar";
import { CalendarAppointmentsTimeline } from "./components/calendar-appointments-timeline";
import { ManageAppointmentDialog } from "./components/manage-appointment-dialog";
import { TransferAppointmentsDialog } from "./components/transfer-appointments-dialog";
import {
    buildAppointmentInsights,
    buildAvailableSlotsByHost,
    buildCalendarAppointmentRows,
    buildCalendarAppointmentsSummary,
    getAssignedHostIds,
    matchesAppointmentSearch,
} from "./utils/calendar-appointments.utils";

const CALENDAR_APPOINTMENTS_MODES = ["view", "transfer"] as const;
type CalendarAppointmentsMode = (typeof CALENDAR_APPOINTMENTS_MODES)[number];

const CALENDAR_APPOINTMENTS_MODE_LABEL_KEYS: Record<CalendarAppointmentsMode, string> = {
    view: "view",
    transfer: "transfer_appointments",
};

const isCalendarAppointmentsMode = (value: string): value is CalendarAppointmentsMode =>
    (CALENDAR_APPOINTMENTS_MODES as readonly string[]).includes(value);

const CalendarAppointmentsSearchSchema = z.object({
    mode: z.enum(CALENDAR_APPOINTMENTS_MODES).optional(),
    eventTypeIds: z.array(z.string()).optional(),
    userId: z.string().optional(),
    date: z.string().optional(),
    /** Appointment to highlight, e.g. when opened from the search dialog. */
    highlight: z.string().optional(),
    /** Deep link (e.g. from the CRM): opens this booking's details on its day. Used together with eventId. */
    bookingId: z.string().optional(),
    eventId: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.CALENDAR_APPOINTMENTS)({
    component: CalendarAppointmentsPage,
    validateSearch: searchValidator(CalendarAppointmentsSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "appointment_management",
                to: APP_ROUTES_TO.CALENDAR_APPOINTMENTS,
            },
            title: "appointment_management",
        },
        permissions: [PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE, PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

function CalendarAppointmentsPage() {
    return <CalendarAppointmentsContent />;
}

function CalendarAppointmentsContent() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [transferOpen, setTransferOpen] = useState(false);
    // View mode: appointment shown in the detail dialog and the one being transferred from it.
    const [managedAppointmentId, setManagedAppointmentId] = useState<string | null>(null);
    const [manageTransferId, setManageTransferId] = useState<string | null>(null);
    const updateBooking = useUpdateBooking();

    const selectedDate = useMemo(() => {
        if (!search.date) {
            return DateTime.now().startOf("day");
        }

        const parsed = DateTime.fromISO(search.date);
        return parsed.isValid ? parsed.startOf("day") : DateTime.now().startOf("day");
    }, [search.date]);

    const dateKey = selectedDate.toFormat("yyyy-MM-dd");
    const mode: CalendarAppointmentsMode = search.mode ?? "view";
    const isTransferMode = mode === "transfer";

    const handleModeChange = useCallback(
        (nextMode: CalendarAppointmentsMode) => {
            navigate({
                to: Route.to,
                search: (prev) => ({ ...prev, mode: nextMode === "view" ? undefined : nextMode }),
                replace: true,
            });

            if (nextMode !== "transfer") {
                setSelectedIds(new Set());
                setTransferOpen(false);
            }
        },
        [navigate]
    );

    const updateSearch = useCallback(
        (updates: { eventTypeIds?: string[] | null; userId?: string | null; date?: string }) => {
            navigate({
                to: Route.to,
                search: (prev) => {
                    const next = { ...prev };
                    // Any filter change ends the highlight of a single appointment.
                    delete next.highlight;

                    if (updates.date !== undefined) {
                        next.date = updates.date;
                    }

                    if (updates.eventTypeIds === null || updates.eventTypeIds?.length === 0) {
                        delete next.eventTypeIds;
                    } else if (updates.eventTypeIds !== undefined) {
                        next.eventTypeIds = updates.eventTypeIds;
                        delete next.userId;
                    }

                    if (updates.userId === null) {
                        delete next.userId;
                    } else if (updates.userId !== undefined) {
                        next.userId = updates.userId;
                        delete next.eventTypeIds;
                    }

                    return next;
                },
                replace: true,
            });
        },
        [navigate]
    );

    useEffect(() => {
        if (search.date) {
            return;
        }

        updateSearch({ date: DateTime.now().toFormat("yyyy-MM-dd") });
    }, [search.date, updateSearch]);

    const { data: eventTypes = [], isLoading: isLoadingEventTypes } = useEventTypes();

    const eventTypesById = useMemo(
        () => new Map(eventTypes.map((eventType) => [eventType.id, eventType])),
        [eventTypes]
    );

    const selectedEventTypeIds = useMemo(() => search.eventTypeIds ?? [], [search.eventTypeIds]);
    const hasEventFilter = selectedEventTypeIds.length > 0;
    const hasFilter = hasEventFilter || Boolean(search.userId);

    const {
        data: fetchedAppointments = [],
        isLoading: isLoadingAppointments,
        isError,
        error,
        refetch,
    } = useBookings(
        {
            dateFrom: dateKey,
            dateTo: dateKey,
            status: BOOKING_STATUS.CONFIRMED,
            // The API filters by a single event; several events are filtered client-side.
            ...(selectedEventTypeIds.length === 1 ? { eventTypeId: selectedEventTypeIds[0] } : {}),
            ...(search.userId ? { hostIds: [search.userId] } : {}),
        },
        { enabled: hasFilter }
    );

    const appointments = useMemo(() => {
        if (selectedEventTypeIds.length <= 1) {
            return fetchedAppointments;
        }

        const eventTypeIdSet = new Set(selectedEventTypeIds);
        return fetchedAppointments.filter((appointment) => eventTypeIdSet.has(appointment.eventTypeId));
    }, [fetchedAppointments, selectedEventTypeIds]);

    const ruleEventTypeIds = useMemo(() => {
        if (hasEventFilter) {
            return selectedEventTypeIds;
        }

        return search.userId ? eventTypes.map((eventType) => eventType.id) : [];
    }, [eventTypes, hasEventFilter, search.userId, selectedEventTypeIds]);

    const { rulesByEventType, isLoading: isLoadingRules } = useEventTypesRules(ruleEventTypeIds);

    // Free slots as generated by call-svc (rules × host schedules − days off − bookings). They are stored
    // in UTC, so the neighbouring days are loaded too and only the slots on the local day are shown.
    const slotHostIds = useMemo(() => (search.userId ? [search.userId] : undefined), [search.userId]);
    const { slots: availableSlots, isLoading: isLoadingSlots } = useAvailableHostSlots({
        eventTypeIds: ruleEventTypeIds,
        startDate: selectedDate.minus({ days: 1 }).toFormat("yyyy-MM-dd"),
        endDate: selectedDate.plus({ days: 1 }).toFormat("yyyy-MM-dd"),
        hostIds: slotHostIds,
        enabled: hasFilter,
    });

    const allowedSlotsByHost = useMemo(
        () => buildAvailableSlotsByHost({ slots: availableSlots, eventTypesById, date: selectedDate }),
        [availableSlots, eventTypesById, selectedDate]
    );

    // Hosts that always get a row: every host assigned to the event, or the selected agent.
    const assignedHostIds = useMemo(() => {
        if (hasEventFilter) {
            return getAssignedHostIds(rulesByEventType);
        }

        return search.userId ? [search.userId] : [];
    }, [hasEventFilter, rulesByEventType, search.userId]);

    const hostIds = useMemo(() => {
        const ids = appointments
            .map((appointment) => appointment.hostId)
            .filter((hostId): hostId is string => Boolean(hostId));
        return [...new Set([...assignedHostIds, ...ids])];
    }, [appointments, assignedHostIds]);

    const { data: teamsData } = useTeams();
    const { data: hostUsers = [] } = useUsersByIdsBatch(hostIds, hostIds.length > 0);

    const hostLabels = useMemo(() => {
        const labels = getUserIdToLabelMap(teamsData?.data ?? []);
        hostUsers.forEach((user) => {
            if (!labels.has(user.id)) {
                labels.set(user.id, user.name || user.email || user.id);
            }
        });
        return labels;
    }, [hostUsers, teamsData?.data]);

    const timelineRows = useMemo(
        () =>
            buildCalendarAppointmentRows({
                appointments,
                hostLabels,
                hostIds: assignedHostIds,
                allowedSlotsByHost,
            }),
        [allowedSlotsByHost, appointments, assignedHostIds, hostLabels]
    );

    const visibleIds = useMemo(() => appointments.map((appointment) => appointment.id), [appointments]);

    // What is typed vs. what was searched: the search only runs on Enter or the search button.
    const [searchDraft, setSearchDraft] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const trimmedSearchTerm = searchTerm.trim();
    const isSearching = trimmedSearchTerm.length > 0;

    const searchMatchIds = useMemo(() => {
        if (!isSearching) {
            return search.highlight ? new Set([search.highlight]) : undefined;
        }

        return new Set(
            appointments
                .filter((appointment) => matchesAppointmentSearch(appointment, searchTerm))
                .map((appointment) => appointment.id)
        );
    }, [appointments, isSearching, search.highlight, searchTerm]);

    // Bring the first match into view when the results change.
    useEffect(() => {
        if (!searchMatchIds?.size) {
            return;
        }

        document
            .querySelector('[data-search-match="true"]')
            ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, [searchMatchIds]);

    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const hasNoMatchesToday =
        Boolean(searchMatchIds) &&
        searchMatchIds?.size === 0 &&
        trimmedSearchTerm.length >= APPOINTMENT_SEARCH_MIN_LENGTH;

    const clearHighlight = useCallback(() => {
        if (search.highlight) {
            navigate({
                to: Route.to,
                search: (prev) => ({ ...prev, highlight: undefined }),
                replace: true,
            });
        }
    }, [navigate, search.highlight]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchDraft(value);
        // Clearing the box (✕, Esc or deleting everything) also clears the results.
        if (!value.trim()) {
            setSearchTerm("");
        }
    }, []);

    // Enter and the search button only search the selected day.
    const handleSearchSubmit = useCallback(() => {
        setSearchTerm(searchDraft.trim());
        clearHighlight();
    }, [clearHighlight, searchDraft]);

    const canSearchAllDates = searchDraft.trim().length >= APPOINTMENT_SEARCH_MIN_LENGTH;

    // Searches the typed value on every date (exact match through the API) in the dialog.
    const handleSearchAllDates = useCallback(() => {
        handleSearchSubmit();
        setSearchDialogOpen(true);
    }, [handleSearchSubmit]);

    const handleSearchResultSelect = useCallback(
        (appointment: Booking) => {
            const localDate = getAppointmentStartDateTime(appointment).toFormat("yyyy-MM-dd");

            setSearchDialogOpen(false);
            setSearchDraft("");
            setSearchTerm("");
            setSelectedIds(new Set());
            navigate({
                to: Route.to,
                search: (prev) => {
                    const { userId: _userId, ...rest } = prev;
                    void _userId;
                    return {
                        ...rest,
                        date: localDate,
                        eventTypeIds: [appointment.eventTypeId],
                        highlight: appointment.id,
                    };
                },
                replace: true,
            });
        },
        [navigate]
    );

    // Deep link: the page shows one day, so look up the booking's day first, then show it like a search result.
    const [linkedBookingId, setLinkedBookingId] = useState<string | null>(null);
    const { data: linkedBooking, isError: isLinkedBookingError } = usePublicBooking(search.bookingId);

    useEffect(() => {
        const { bookingId, eventId } = search;
        if (!bookingId || (!linkedBooking && !isLinkedBookingError)) {
            return;
        }

        const isOpenable = Boolean(eventId && linkedBooking?.status === BOOKING_STATUS.CONFIRMED);
        if (!isOpenable) {
            toast.error(t("appointment_link_not_found"));
        }

        setLinkedBookingId(isOpenable ? bookingId : null);
        setSelectedIds(new Set());
        navigate({
            to: Route.to,
            search: (prev) => {
                const { bookingId: _bookingId, eventId: _eventId, userId: _userId, mode: _mode, ...rest } = prev;
                void _bookingId;
                void _eventId;
                void _userId;
                void _mode;

                if (!isOpenable || !linkedBooking || !eventId) {
                    return rest;
                }

                return {
                    ...rest,
                    date: getAppointmentStartDateTime(linkedBooking).toFormat("yyyy-MM-dd"),
                    eventTypeIds: [eventId],
                    highlight: bookingId,
                };
            },
            replace: true,
        });
    }, [isLinkedBookingError, linkedBooking, navigate, search, t]);

    const isViewMode = mode === "view";
    const { selectedBrand, setSelectedBrand, isCallActive } = useCall();
    // Appointment waiting for the brand switch confirmation before its details open.
    const [brandSwitchRequest, setBrandSwitchRequest] = useState<{
        appointmentId: string;
        brandName: BrandName;
    } | null>(null);

    // The details (CRM data) are loaded for the selected brand, so ask to switch when the event is another brand.
    const handleOpenAppointment = useCallback(
        (appointmentId: string) => {
            const appointment = appointments.find((item) => item.id === appointmentId);
            const brandName = appointment ? eventTypesById.get(appointment.eventTypeId)?.brandName : undefined;

            if (brandName && brandName !== selectedBrand) {
                setBrandSwitchRequest({ appointmentId, brandName });
                return;
            }

            setManagedAppointmentId(appointmentId);
        },
        [appointments, eventTypesById, selectedBrand]
    );

    const managedAppointment = useMemo(
        () => appointments.find((appointment) => appointment.id === managedAppointmentId) ?? null,
        [appointments, managedAppointmentId]
    );

    // Close the dialog once the appointment is gone (e.g. cancelled) or the mode changes.
    useEffect(() => {
        if (!managedAppointmentId) {
            return;
        }

        if (!isViewMode || (!isLoadingAppointments && !managedAppointment)) {
            setManagedAppointmentId(null);
        }
    }, [isLoadingAppointments, isViewMode, managedAppointment, managedAppointmentId]);

    const appointmentInsights = useMemo(() => buildAppointmentInsights(appointments), [appointments]);

    const appointmentsSummary = useMemo(
        () => buildCalendarAppointmentsSummary(appointments, appointmentInsights),
        [appointmentInsights, appointments]
    );

    useEffect(() => {
        setSelectedIds((current) => {
            const next = new Set([...current].filter((id) => visibleIds.includes(id)));
            return next.size === current.size ? current : next;
        });
    }, [visibleIds]);

    const handleDateChange = useCallback(
        (nextDate: DateTime) => {
            updateSearch({ date: nextDate.toFormat("yyyy-MM-dd") });
            setSelectedIds(new Set());
        },
        [updateSearch]
    );

    const handleToggleAppointment = useCallback((appointmentId: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(appointmentId)) {
                next.delete(appointmentId);
            } else {
                next.add(appointmentId);
            }
            return next;
        });
    }, []);

    const handleToggleRow = useCallback((appointmentIds: string[]) => {
        setSelectedIds((current) => {
            const allSelected = appointmentIds.every((id) => current.has(id));
            const next = new Set(current);

            appointmentIds.forEach((id) => {
                if (allSelected) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
            });

            return next;
        });
    }, []);

    const handleTransfer = useCallback(
        async (targetUserId: string | null, forceTransfer = false, appointmentIds?: string[]) => {
            const ids = appointmentIds ?? [...selectedIds];
            try {
                for (const id of ids) {
                    await updateBooking.mutateAsync({
                        id,
                        hostId: targetUserId,
                        ...(forceTransfer && { forceTransfer: true }),
                    });
                }
                toast.success(t(targetUserId ? "appointments_transferred" : "appointments_unassigned"));
                if (!appointmentIds) {
                    setSelectedIds(new Set());
                }
                setTransferOpen(false);
                setManageTransferId(null);
            } catch (error) {
                toast.error(
                    new ApiError(error).getMessage(
                        t,
                        targetUserId ? "failed_to_transfer_appointments" : "failed_to_unassign_appointments"
                    )
                );
            }
        },
        [selectedIds, t, updateBooking]
    );

    const isLoading = isLoadingEventTypes || (hasFilter && (isLoadingAppointments || isLoadingRules || isLoadingSlots));

    // Open the linked booking once its day has loaded.
    useEffect(() => {
        if (!linkedBookingId || isLoading || search.bookingId) {
            return;
        }

        setLinkedBookingId(null);
        if (appointments.some((appointment) => appointment.id === linkedBookingId)) {
            handleOpenAppointment(linkedBookingId);
        } else {
            toast.error(t("appointment_link_not_found"));
        }
    }, [appointments, handleOpenAppointment, isLoading, linkedBookingId, search.bookingId, t]);

    return (
        <div className="flex w-full flex-col gap-4">
            <PageActions>
                <div className="w-[280px]">
                    <MultiSelect
                        values={selectedEventTypeIds}
                        onValuesChange={(eventTypeIds) => updateSearch({ eventTypeIds })}
                    >
                        <MultiSelectTrigger asChild>
                            <Button
                                variant="outline"
                                className="h-9 w-full justify-between font-normal"
                                disabled={isLoadingEventTypes || eventTypes.length === 0}
                            >
                                <MultiSelectValue placeholder={t("select_event")} overflowBehavior="cutoff" />
                                {hasEventFilter ? (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        aria-label={t("clear_selection")}
                                        className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        onPointerDown={(event) => event.stopPropagation()}
                                        onClick={(event) => {
                                            // Prevent the popover trigger from opening when clearing.
                                            event.preventDefault();
                                            event.stopPropagation();
                                            updateSearch({ eventTypeIds: null });
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                updateSearch({ eventTypeIds: null });
                                            }
                                        }}
                                    >
                                        <Icon icon={TbX} size={14} />
                                    </span>
                                ) : (
                                    <Icon icon={TbChevronDown} className="shrink-0 opacity-50" />
                                )}
                            </Button>
                        </MultiSelectTrigger>
                        <MultiSelectContent>
                            <MultiSelectGroup>
                                {eventTypes.map((eventType) => (
                                    <MultiSelectItem key={eventType.id} value={eventType.id}>
                                        <span className="flex items-center gap-2">
                                            {eventType.color ? (
                                                <span
                                                    className="size-2.5 shrink-0 rounded-full"
                                                    style={{ backgroundColor: eventType.color }}
                                                />
                                            ) : null}
                                            {eventType.title}
                                        </span>
                                    </MultiSelectItem>
                                ))}
                            </MultiSelectGroup>
                        </MultiSelectContent>
                    </MultiSelect>
                </div>

                <div className="w-[280px]">
                    <TeamMemberSelector
                        selectedMemberId={search.userId}
                        onMemberChange={(userId) => updateSearch({ userId })}
                        labels={{
                            selectItem: "select_agent",
                            resetSelection: "all_agents",
                        }}
                    />
                </div>

                <div className="inline-flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 p-0"
                        aria-label={t("previous_day")}
                        onClick={() => handleDateChange(selectedDate.minus({ days: 1 }))}
                    >
                        <Icon icon={TbChevronLeft} size={16} />
                    </Button>
                    <DatePicker
                        date={selectedDate.toJSDate()}
                        onDateChange={(date) => {
                            if (!date) {
                                return;
                            }

                            handleDateChange(DateTime.fromJSDate(date));
                        }}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 p-0"
                        aria-label={t("next_day")}
                        onClick={() => handleDateChange(selectedDate.plus({ days: 1 }))}
                    >
                        <Icon icon={TbChevronRight} size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={selectedDate.hasSame(DateTime.now(), "day")}
                        onClick={() => handleDateChange(DateTime.now())}
                    >
                        {t("today")}
                    </Button>
                </div>
            </PageActions>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex w-full items-center gap-2 sm:w-auto">
                    <AppointmentSearchInput
                        value={searchDraft}
                        onChange={handleSearchChange}
                        onSubmit={handleSearchSubmit}
                        disabled={!hasFilter}
                        className="w-full sm:w-[420px]"
                    />
                    {isSearching && searchMatchIds && searchMatchIds.size > 0 ? (
                        <span className="whitespace-nowrap rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium tabular-nums text-amber-700 dark:text-amber-300">
                            {t("search_matches_count", { count: searchMatchIds.size })}
                        </span>
                    ) : null}
                    <Button
                        type="button"
                        // Highlighted when the search found nothing on the selected day.
                        variant={hasNoMatchesToday ? "default" : "outline"}
                        className="h-9 shrink-0 gap-1.5"
                        disabled={!hasFilter || !canSearchAllDates}
                        onClick={handleSearchAllDates}
                    >
                        <Icon icon={TbCalendarSearch} size={16} />
                        {t("search_all_dates")}
                    </Button>
                </div>
                <Tabs
                    value={mode}
                    onValueChange={(nextMode) => {
                        if (isCalendarAppointmentsMode(nextMode)) {
                            handleModeChange(nextMode);
                        }
                    }}
                    className="ml-auto"
                >
                    <TabsList className="grid h-9 grid-cols-2 gap-1 rounded-lg border border-border/60 bg-muted/40 p-1">
                        {CALENDAR_APPOINTMENTS_MODES.map((modeOption) => (
                            <TabsTrigger
                                key={modeOption}
                                value={modeOption}
                                className="h-full rounded-md px-4 text-xs font-medium transition-all duration-200 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:text-sm"
                            >
                                {t(CALENDAR_APPOINTMENTS_MODE_LABEL_KEYS[modeOption])}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            {isTransferMode && hasFilter ? (
                <div className="flex justify-end">
                    <CalendarAppointmentsSelectionBar
                        selectedCount={selectedIds.size}
                        totalCount={appointments.length}
                        isPending={updateBooking.isPending}
                        onSelectAll={() => setSelectedIds(new Set(visibleIds))}
                        onClear={() => setSelectedIds(new Set())}
                        onTransfer={() => setTransferOpen(true)}
                        onUnassign={() => {
                            void handleTransfer(null);
                        }}
                    />
                </div>
            ) : null}

            {!hasFilter && !isLoadingEventTypes ? <EmptyState variant="simple" title="select_event_or_agent" /> : null}

            {isLoading ? (
                <div className="flex flex-1 items-center justify-center py-16">
                    <Spinner />
                </div>
            ) : null}

            {isError ? (
                <ErrorState
                    title={t("error")}
                    message={error instanceof Error ? error.message : t("connection_error")}
                    onRetry={() => {
                        void refetch();
                    }}
                    retryLabel={t("try_again")}
                />
            ) : null}

            {hasFilter && !isLoading && !isError && timelineRows.length === 0 ? (
                <EmptyState variant="simple" title="no_appointments_for_filters" />
            ) : null}

            {isViewMode && hasFilter && !isLoading && !isError && appointments.length > 0 ? (
                <CalendarAppointmentsSummaryBar summary={appointmentsSummary} />
            ) : null}

            {hasFilter && !isLoading && !isError && timelineRows.length > 0 ? (
                <CalendarAppointmentsTimeline
                    rows={timelineRows}
                    eventTypesById={eventTypesById}
                    selectedIds={isTransferMode ? selectedIds : undefined}
                    onToggleAppointment={isTransferMode ? handleToggleAppointment : undefined}
                    onToggleRow={isTransferMode ? handleToggleRow : undefined}
                    insights={isViewMode ? appointmentInsights : undefined}
                    onOpenAppointment={isViewMode ? handleOpenAppointment : undefined}
                    pulsePending={isViewMode && selectedDate.hasSame(DateTime.now(), "day")}
                    searchMatchIds={searchMatchIds}
                />
            ) : null}

            <AppointmentSearchDialog
                open={searchDialogOpen}
                onOpenChange={setSearchDialogOpen}
                searchTerm={trimmedSearchTerm}
                currentDate={selectedDate}
                hasMatchesOnDate={Boolean(searchMatchIds?.size)}
                eventTypesById={eventTypesById}
                hostLabels={hostLabels}
                onSelect={handleSearchResultSelect}
            />

            <AppointmentBrandSwitchDialog
                appointmentBrand={brandSwitchRequest?.brandName ?? null}
                currentBrand={selectedBrand ?? null}
                isSwitchDisabled={isCallActive()}
                onSwitch={(brandName) => {
                    setSelectedBrand(brandName);
                    setManagedAppointmentId(brandSwitchRequest?.appointmentId ?? null);
                    setBrandSwitchRequest(null);
                }}
                onKeepCurrent={() => {
                    setManagedAppointmentId(brandSwitchRequest?.appointmentId ?? null);
                    setBrandSwitchRequest(null);
                }}
                onCancel={() => setBrandSwitchRequest(null)}
            />

            <ManageAppointmentDialog
                appointment={managedAppointment}
                hostLabel={managedAppointment?.hostId ? hostLabels.get(managedAppointment.hostId) : undefined}
                isPending={updateBooking.isPending}
                onOpenChange={(open) => {
                    if (!open) {
                        setManagedAppointmentId(null);
                    }
                }}
                onTransfer={(appointment) => setManageTransferId(appointment.id)}
                onUnassign={(appointment) => {
                    void handleTransfer(null, false, [appointment.id]);
                }}
            />

            <TransferAppointmentsDialog
                open={(isTransferMode && transferOpen) || Boolean(manageTransferId)}
                selectedCount={manageTransferId ? 1 : selectedIds.size}
                isPending={updateBooking.isPending}
                onOpenChange={(open) => {
                    if (!open) {
                        setTransferOpen(false);
                        setManageTransferId(null);
                    }
                }}
                onConfirm={(userId, forceTransfer) => {
                    void handleTransfer(userId, forceTransfer, manageTransferId ? [manageTransferId] : undefined);
                }}
            />
        </div>
    );
}
